/**
 * ExamApplication Types for MEV-Med Frontend
 * Type definitions for exam application management across organizational levels
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão de aplicações de exames em 4 níveis organizacionais
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Application levels in the organizational hierarchy
 */
export enum ApplicationLevel {
  COMPANY = 'company',
  DEPARTMENT = 'department',
  OCCUPATION = 'occupation',
  EMPLOYEE = 'employee',
}

/**
 * Expiration status for exam applications
 */
export enum ExpirationStatus {
  OK = 'OK',
  EXPIRING_SOON = 'EXPIRING_SOON',
  EXPIRED = 'EXPIRED',
}

// ============================================================================
// APPLICATION DATA INTERFACES
// ============================================================================

/**
 * Data for creating/updating an exam application
 * Used in apply and update operations
 */
export interface ApplicationData {
  periodicityMonths?: number | null;
  lastExamDate?: string | null;
  nextExamDate?: string | null;
  isRequired?: boolean;
  observations?: string | null;
}

/**
 * Request payload for applying an exam
 */
export interface ApplyExamRequest {
  examId: number;
  periodicityMonths?: number | null;
  lastExamDate?: string | null;
  nextExamDate?: string | null;
  isRequired?: boolean;
  observations?: string | null;
}

/**
 * Request payload for updating an application
 */
export interface UpdateApplicationRequest {
  periodicityMonths?: number | null;
  lastExamDate?: string | null;
  nextExamDate?: string | null;
  isRequired?: boolean;
  observations?: string | null;
}

// ============================================================================
// EXAM WITH APPLICATION (Applied Exams)
// ============================================================================

/**
 * Exam with application details
 * Represents an exam that has been applied to a target (company, department, occupation, or employee)
 */
export interface ExamWithApplication {
  id: number;
  examId: number;
  examName: string;
  esocialCode?: string | null;
  tussCode?: string | null;
  periodicityMonths?: number | null;
  lastExamDate?: string | null;
  nextExamDate?: string | null;
  isRequired: boolean;
  observations?: string | null;
  isExpired: boolean;
  isExpiringSoon: boolean;
  daysUntilExpiration?: number | null;
  applicationLevel: ApplicationLevel;
  createdAt: string;
  updatedAt: string;
}

/**
 * Computed expiration status based on flags
 */
export function getExpirationStatus(exam: ExamWithApplication): ExpirationStatus {
  if (exam.isExpired) return ExpirationStatus.EXPIRED;
  if (exam.isExpiringSoon) return ExpirationStatus.EXPIRING_SOON;
  return ExpirationStatus.OK;
}

// ============================================================================
// AVAILABLE EXAM (Not Yet Applied)
// ============================================================================

/**
 * Available exam for application
 * Represents an exam that can be applied to a target
 */
export interface AvailableExam {
  id: number;
  examName: string;
  esocialCode?: string | null;
  tussCode?: string | null;
  defaultFrequency?: string | null;
}

// ============================================================================
// EXPIRATION ALERTS
// ============================================================================

/**
 * Exam that is expiring soon
 */
export interface ExpiringExam {
  applicationId: number;
  examId: number;
  examName: string;
  targetId: number;
  targetType: ApplicationLevel;
  targetName: string;
  nextExamDate: string;
  daysUntilExpiration: number;
}

/**
 * Exam that has expired
 */
export interface ExpiredExam extends ExpiringExam {
  daysOverdue: number;
}

/**
 * Expiration alerts response
 */
export interface ExpirationAlerts {
  expiring: ExpiringExam[];
  expired: ExpiredExam[];
}

// ============================================================================
// API RESPONSE INTERFACES
// ============================================================================

/**
 * Response for get exams endpoint (applied + available)
 */
export interface ExamsForTargetResponse {
  applied: ExamWithApplication[];
  available: AvailableExam[];
}

/**
 * Generic API success response
 */
export interface ExamApplicationSuccessResponse {
  message: string;
  code: string;
}

/**
 * API error response
 */
export interface ExamApplicationErrorResponse {
  error: string;
  code: string;
}

// ============================================================================
// TARGET INFORMATION
// ============================================================================

/**
 * Target entity for application context
 */
export interface ApplicationTarget {
  id: number;
  name: string;
  level: ApplicationLevel;
  parentId?: number;
  parentName?: string;
}

/**
 * Hierarchical target with full path
 */
export interface ApplicationTargetWithPath extends ApplicationTarget {
  companyId: number;
  companyName: string;
  departmentId?: number;
  departmentName?: string;
  occupationId?: number;
  occupationName?: string;
}

// ============================================================================
// UI COMPONENT PROPS
// ============================================================================

/**
 * Props for DualTransferList component
 */
export interface ExamTransferListProps {
  applied: ExamWithApplication[];
  available: AvailableExam[];
  onApply: (examIds: number[], data?: ApplicationData) => Promise<void>;
  onRemove: (examIds: number[]) => Promise<void>;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * Props for ExamApplicationModal (for setting application details)
 */
export interface ExamApplicationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: ApplicationData) => Promise<void>;
  exam: AvailableExam | ExamWithApplication | null;
  mode: 'apply' | 'edit';
  loading?: boolean;
}

/**
 * Props for ExpirationBadge component
 */
export interface ExpirationBadgeProps {
  status: ExpirationStatus;
  daysUntilExpiration?: number | null;
  showDays?: boolean;
}

/**
 * Props for AlertsTable component
 */
export interface ExamAlertsTableProps {
  alerts: ExpirationAlerts;
  onExamClick?: (alert: ExpiringExam | ExpiredExam) => void;
  loading?: boolean;
}

// ============================================================================
// FILTER INTERFACES
// ============================================================================

/**
 * Filters for exam applications list
 */
export interface ExamApplicationFilters {
  targetLevel?: ApplicationLevel;
  targetId?: number;
  expirationStatus?: ExpirationStatus;
  isRequired?: boolean;
  search?: string;
}

/**
 * Filters for expiration alerts
 */
export interface ExpirationAlertsFilters {
  companyId: number;
  daysAhead?: number;
  targetType?: ApplicationLevel;
}

// ============================================================================
// LABELS AND CONSTANTS
// ============================================================================

/**
 * Labels for application levels (Portuguese)
 */
export const ApplicationLevelLabels: Record<ApplicationLevel, string> = {
  [ApplicationLevel.COMPANY]: 'Empresa',
  [ApplicationLevel.DEPARTMENT]: 'Setor',
  [ApplicationLevel.OCCUPATION]: 'Cargo',
  [ApplicationLevel.EMPLOYEE]: 'Funcionário',
};

/**
 * Labels for expiration status (Portuguese)
 */
export const ExpirationStatusLabels: Record<ExpirationStatus, string> = {
  [ExpirationStatus.OK]: 'Em dia',
  [ExpirationStatus.EXPIRING_SOON]: 'Vencendo',
  [ExpirationStatus.EXPIRED]: 'Vencido',
};

/**
 * Colors for expiration status badges
 */
export const ExpirationStatusColors: Record<ExpirationStatus, 'success' | 'warning' | 'error'> = {
  [ExpirationStatus.OK]: 'success',
  [ExpirationStatus.EXPIRING_SOON]: 'warning',
  [ExpirationStatus.EXPIRED]: 'error',
};

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Type guard for checking if an exam is applied
 */
export function isExamWithApplication(
  exam: ExamWithApplication | AvailableExam
): exam is ExamWithApplication {
  return 'applicationLevel' in exam && 'isExpired' in exam;
}

/**
 * Type guard for checking if alert is expired
 */
export function isExpiredExam(
  alert: ExpiringExam | ExpiredExam
): alert is ExpiredExam {
  return 'daysOverdue' in alert;
}

/**
 * Format days until expiration for display
 */
export function formatDaysUntilExpiration(days: number | null | undefined): string {
  if (days === null || days === undefined) {
    return 'N/A';
  }
  if (days < 0) {
    const overdue = Math.abs(days);
    return `${overdue} dia${overdue !== 1 ? 's' : ''} vencido`;
  }
  if (days === 0) {
    return 'Vence hoje';
  }
  return `${days} dia${days !== 1 ? 's' : ''}`;
}
